/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback } from 'react';
import { Box, Typography, CircularProgress, Stack, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { DealTableView } from '../DealTableView';
import { KanbanView } from '../KanbanView';
import { usePipelineContext } from '@/context/pipelineContext';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import PreviewDrawer from '@/components/Drawers/PreviewDrawer';
import { DealPipelineTabs } from '@/components/views/DealPipelineTabs';
import { KanbanViewKPIHeader } from '@/components/views/KanbanView/KanbanViewKPIHeader';
import InvoiceTotalsHeader from '@/components/tables/InsightDebtorsTable/InvoiceTotalHeader';

export const DealPipelineView = ({ pipelineId }) => {
	const theme = useTheme();

	const isRenewals = pipelineId === process.env.REACT_APP_PIPELINE_RENEWALS;
	const isDebtors = pipelineId === process.env.REACT_APP_PIPELINE_DEBTORS;
	const isNewBusiness = pipelineId === process.env.REACT_APP_PIPELINE_NEW_BUSINESS;

	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);

	const [searchParams, setSearchParams] = useSearchParams();
	const currentTab = searchParams.get('tab') || (isDebtors ? 'awaiting-payment' : 'active');

	const handleTabChange = useCallback(
		(tab) => {
			const next = new URLSearchParams(searchParams);
			next.set('tab', tab);
			setSearchParams(next);
		},
		[searchParams, setSearchParams],
	);

	const { kanbanView } = useSelector((state) => state.userPrefs);

	const { isSuccess, isError } = usePipelineContext(pipelineId);

	const loading = !isSuccess && !isError;

	const isInvoiceReportTab = isDebtors && currentTab === 'invoice-report';

	const shouldHideKanban =
		isInvoiceReportTab ||
		(isRenewals && (currentTab === 'policies' || currentTab === 'archived'));

	return (
		<Box pt={1}>
			{loading ? (
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<CircularProgress />
				</div>
			) : isError ? (
				<Box>
					<Typography textAlign='center'>
						There was an issue loading this pipeline. Please try again.
					</Typography>
				</Box>
			) : (
				<Stack>
					<DealPipelineTabs
						pipelineId={pipelineId}
						currentTab={currentTab}
						onTabChange={handleTabChange}
					/>
					<Stack spacing={2} sx={{ pt: 1 }}>
						{((isNewBusiness && showNewBusinessKPI) || isInvoiceReportTab) && (
							<Box px={3}>
								<Box
									sx={{
										mx: 3,
										p: 1,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: '8px',
										backgroundColor: theme.palette.background.paper,
									}}
								>
									{isNewBusiness ? (
										<KanbanViewKPIHeader pipelineId={pipelineId} />
									) : (
										<InvoiceTotalsHeader />
									)}
								</Box>
							</Box>
						)}
						{kanbanView && !shouldHideKanban ? (
							<KanbanView pipelineId={pipelineId} />
						) : (
							<DealTableView pipelineId={pipelineId} currentTab={currentTab} />
						)}
					</Stack>
				</Stack>
			)}
			{!isError && <PreviewDrawer />}
		</Box>
	);
};

DealPipelineView.displayName = 'DealPipelineView';
